/**
 * Operaciones de escritura para los catálogos contables + listados PAGINADOS
 * del gestor admin. Los pickers del wizard siguen usando los listados planos
 * de `features/catalogos` (mismos endpoints SIN `page` · dualidad de shape
 * documentada en CatalogosEndpoints.cs).
 */
import { axiosClient } from "@/shared/api/axios-client";
import type {
  CentroDeCosto,
  CentroDeCostoConOrganizaciones,
  CuentaContable,
  CuentaContableConOrganizaciones,
  OrganizacionDisponible,
  Paginado,
} from "@/lib/types";

/** Alta de CeCo · obliga elegir al menos una organización (relación M:N). */
export interface CrearCecoInput {
  codigo: string;
  nombre: string;
  numerosSociedad: string[];
}

/** Edición de CeCo · nombre + conjunto de organizaciones (se sincroniza). */
export interface EditarCecoInput {
  nombre: string;
  numerosSociedad: string[];
}

export interface ListarCatalogoContableParams {
  busqueda?: string;
  page?: number;
  pageSize?: number;
}

export async function crearCentroDeCosto(
  input: CrearCecoInput
): Promise<CentroDeCosto> {
  const { data } = await axiosClient.post<CentroDeCosto>(
    "/api/admin/centros-de-costo",
    input
  );
  return data;
}

export async function editarCentroDeCosto(
  id: string,
  input: EditarCecoInput
): Promise<CentroDeCosto> {
  const { data } = await axiosClient.patch<CentroDeCosto>(
    `/api/admin/centros-de-costo/${id}`,
    input
  );
  return data;
}

/** Listado del gestor de relaciones · CeCos con su(s) organización(es), paginado. */
export async function listarCentrosDeCostoConOrganizaciones(
  params: ListarCatalogoContableParams = {}
): Promise<Paginado<CentroDeCostoConOrganizaciones>> {
  const { data } = await axiosClient.get<Paginado<CentroDeCostoConOrganizaciones>>(
    "/api/admin/centros-de-costo/con-organizaciones",
    { params: { ...params, page: params.page ?? 1 } }
  );
  return data;
}

/** Organizaciones activas asignables a un CeCo · opciones del multiselect. */
export async function listarOrganizacionesDisponiblesParaCeco(): Promise<OrganizacionDisponible[]> {
  const { data } = await axiosClient.get<OrganizacionDisponible[]>(
    "/api/admin/centros-de-costo/organizaciones-disponibles"
  );
  return data;
}

/** D-8 · listado del gestor · cuentas con su(s) organización(es), paginado. */
export async function listarCuentasContablesConOrganizaciones(
  params: ListarCatalogoContableParams = {}
): Promise<Paginado<CuentaContableConOrganizaciones>> {
  const { data } = await axiosClient.get<Paginado<CuentaContableConOrganizaciones>>(
    "/api/admin/cuentas-contables/con-organizaciones",
    { params: { ...params, page: params.page ?? 1 } }
  );
  return data;
}

/** D-8 · alta de cuenta · obliga elegir al menos una organización (mismo contrato que el CeCo). */
export async function crearCuentaContable(
  input: CrearCecoInput
): Promise<CuentaContable> {
  const { data } = await axiosClient.post<CuentaContable>(
    "/api/admin/cuentas-contables",
    input
  );
  return data;
}

export async function editarCuentaContable(
  id: string,
  input: EditarCecoInput
): Promise<CuentaContable> {
  const { data } = await axiosClient.patch<CuentaContable>(
    `/api/admin/cuentas-contables/${id}`,
    input
  );
  return data;
}

// --- Importación masiva desde Excel (CeCo / Cuentas) -------------------------

/**
 * Una fila de la importación · el nombre es opcional en el archivo (se valida en el backend).
 * `numeroSociedad` es la organización del vínculo (M:N) · CeCos y, desde D-8, cuentas contables.
 */
export interface FilaImportacionItemCatalogo {
  codigo: string;
  nombre: string;
  numeroSociedad?: string;
}

/** Detalle por fila del preview · qué se hará con cada renglón. */
export interface PreviewFilaItemCatalogo {
  renglon: number;
  codigo: string;
  nombre: string | null;
  estado: "Crear" | "Actualizar" | "Omitir";
  motivo: string | null;
  numeroSociedad?: string | null;
}

/** Resumen que devuelve el backend tras importar (real y dry-run). */
export interface ResultadoImportacionItemCatalogo {
  creados: number;
  actualizados: number;
  omitidos: number;
  errores: string[];
  detalle: PreviewFilaItemCatalogo[];
}

/**
 * Importa (o valida en dry-run) los centros de costo. Con `soloValidar=true` el
 * backend corre la misma lógica SIN persistir y devuelve el detalle por fila.
 */
export async function importarCentrosDeCosto(
  filas: FilaImportacionItemCatalogo[],
  soloValidar = false
): Promise<ResultadoImportacionItemCatalogo> {
  const { data } = await axiosClient.post<ResultadoImportacionItemCatalogo>(
    "/api/admin/centros-de-costo/importar",
    { filas, soloValidar }
  );
  return data;
}

/** Importa (o valida en dry-run) las cuentas contables. Ver `importarCentrosDeCosto`. */
export async function importarCuentasContables(
  filas: FilaImportacionItemCatalogo[],
  soloValidar = false
): Promise<ResultadoImportacionItemCatalogo> {
  const { data } = await axiosClient.post<ResultadoImportacionItemCatalogo>(
    "/api/admin/cuentas-contables/importar",
    { filas, soloValidar }
  );
  return data;
}
